(() => {
  'use strict';

  const ELEMENT_NAME = 'age-verification-pop-up';
  const MODAL_ELEMENT_NAME = 'modal-dialog';
  const COOKIE_LIFETIME_DAYS = 3650;

  /**
   * Sets a first-party cookie used to remember a successful age confirmation.
   * Keeping these helpers private prevents collisions with app scripts that use
   * generic global names such as `setCookie` and `getCookie`.
   * @param {string} name - Cookie name.
   * @param {string|boolean} value - Cookie value.
   * @param {number} days - Number of days until the cookie expires.
   */
  function setAgeVerificationCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));

    const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';

    try {
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/; SameSite=Lax${secureAttribute}`;
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets a decoded first-party cookie value.
   * @param {string} name - Cookie name.
   * @returns {?string}
   */
  function getAgeVerificationCookie(name) {
    const encodedName = `${encodeURIComponent(name)}=`;
    let matchingCookie;

    try {
      matchingCookie = document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(encodedName));
    } catch (error) {
      return null;
    }

    if (!matchingCookie) return null;

    try {
      return decodeURIComponent(matchingCookie.slice(encodedName.length));
    } catch (error) {
      return matchingCookie.slice(encodedName.length);
    }
  }

  /**
   * Defines the age verification component after the base modal is available.
   * The second registration check prevents duplicate-definition errors if the
   * section script is evaluated more than once by the theme editor or an app.
   */
  function defineAgeVerificationPopUp() {
    if (customElements.get(ELEMENT_NAME)) return;

    /* global Modal */
    if (typeof Modal !== 'function') return;

    class AgeVerificationPopUp extends Modal {
      constructor() {
        super();

        this.cookie = `${this.id}-dismissed`;
        this.confirmedForSession = false;
        this.editorListenersAttached = false;
        this.boundSectionSelect = this.handleSectionSelect.bind(this);
        this.boundSectionDeselect = this.handleSectionDeselect.bind(this);
      }

      connectedCallback() {
        // Refresh the cookie name in case the section was created before its ID
        // was assigned, then initialise only the listeners needed for this mode.
        this.cookie = `${this.id}-dismissed`;

        if (window.Shopify && window.Shopify.designMode) {
          if (!this.editorListenersAttached) {
            document.addEventListener('shopify:section:select', this.boundSectionSelect);
            document.addEventListener('shopify:section:deselect', this.boundSectionDeselect);
            this.editorListenersAttached = true;
          }
        } else if (!this.confirmedForSession && !getAgeVerificationCookie(this.cookie)) {
          this.open();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:select', this.boundSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.boundSectionDeselect);
        this.editorListenersAttached = false;
      }

      /**
       * Opens the modal and prevents Escape from bypassing age confirmation.
       * @param {Element} opener - Modal opener element.
       */
      open(opener) {
        super.open(opener);
        if (this.keyupHandler) this.removeEventListener('keyup', this.keyupHandler);
      }

      /**
       * Opens this section while it is selected in the theme editor.
       * @param {CustomEvent} evt - Shopify section event.
       */
      handleSectionSelect(evt) {
        if (evt.target === this.closest('.shopify-section')) this.open();
      }

      /**
       * Closes this section while it is deselected in the theme editor.
       */
      handleSectionDeselect() {
        this.close();
      }

      /**
       * Handles the under-age action.
       * @param {HTMLButtonElement} button - Under-age button.
       */
      handleCancelClick(button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        if (window.history.length > 1 && document.referrer) {
          window.history.back();
          return;
        }

        window.location.replace('https://www.responsibility.org/');
      }

      /**
       * Handles both age-gate buttons through event delegation. `closest`
       * supports button markup containing icons or spans and avoids fragile
       * direct listeners on optional elements.
       * @param {MouseEvent} evt - Click event.
       */
      handleClick(evt) {
        const target = evt.target instanceof Element ? evt.target : null;
        if (!target) return;

        const cancelButton = target.closest('.js-cancel-button');
        if (cancelButton && this.contains(cancelButton)) {
          evt.preventDefault();
          this.handleCancelClick(cancelButton);
          return;
        }

        const closeButton = target.closest('.js-close-modal');
        if (!closeButton || !this.contains(closeButton)) return;

        evt.preventDefault();
        this.confirmedForSession = true;
        setAgeVerificationCookie(this.cookie, true, COOKIE_LIFETIME_DAYS);
        this.close();
      }
    }

    customElements.define(ELEMENT_NAME, AgeVerificationPopUp);
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.whenDefined(MODAL_ELEMENT_NAME).then(defineAgeVerificationPopUp);
  }
})();
